import { Temporal } from '@imago/api-client'
import { CancelButton, cancelDialog, Dialog, IntentMutation, SubmitButton } from '@imago/api-client-for-react'
import { BuyIntentRequest, InvoiceID, OnboardingConsentAcceptance, PayIntentRequest, ProductID, StripeIntent } from '@imago/model'
import { use } from 'react'
import { useDeclareImagoIntent, useOwns, useProduct, useStripeFinalizationState } from '../api.ts'
import { StripeElements, StripePaymentElement, useConfirmPayment } from './Stripe.tsx'
export { useOwns }

const locale = 'en-GB'

interface SubscribeIntentRequest {
	verb: 'subscribe'
}

export function PaymentDialog({mutation}: {
	mutation: IntentMutation<StripeIntent | null> & {request: BuyIntentRequest | SubscribeIntentRequest | PayIntentRequest}
}) {
	return <Dialog mutation={mutation}>
		{mutation.intent === null ? <></> :
			<Pay request={mutation.request} intent={mutation.intent}/>
		}
	</Dialog>
}

function Pay({request, intent}: {
	request: BuyIntentRequest | SubscribeIntentRequest | PayIntentRequest
	intent: Promise<StripeIntent | null>
}) {
	return <StripeElements intent={intent}>
		<_PaymentDialogContent request={request} intent={intent}/>
	</StripeElements>
}

function _PaymentDialogContent({request, intent: intentPromise}: {
	request: BuyIntentRequest | SubscribeIntentRequest | PayIntentRequest
	intent: Promise<StripeIntent | null>
}) {
	const intent = use(intentPromise)
	const product = useProduct(request.verb === 'buy' ? request.productID : undefined)

	const confirm = useConfirmPayment()

	if (!intent) return

	const tz = Temporal.Now.timeZoneId()
	const {amountToPay, amountNote} = intent.stripe_secret

	return <form>
		<h4>
			{
				request.verb === 'subscribe' ? `Subscribe to Imago Care` :
				request.verb === 'buy' ? `Buy ${product?.title}` :
				request.verb === 'pay' ? `Pay invoice` :
				(() => {request satisfies never; throw new Error('unexpected')})()
			}
		</h4>
		<div>
			{amountNote ? <p>{amountNote.split(' ').map(word => {
				// Yes, this is funny.
				// Maybe it's because 3:34 AM... No, that's a normal time for me.
				// I really don't want this code to understand the calculation algorithm, so I really want the string to come from the backend.
				// But no, backend can't know the user's timezone.
				// So yeah, splitting on spaces and replacing timestamps was so smart.
				// Until the dot happened.
				// Yeah, I could probably do it in a better way, maybe some regex or something.
				// I don't care.
				// This works.
				// And the chance of breaking is negligibly small.
				// And if it breaks, nobody will die, the user will just see the ISO time string.
				// Should anybody care?
				// Not really.
				// Unless the future AI will judge the humanity upon this code.
				// Then I'm sorry, we're dead.
				//
				// Update 3:56 AM: I've reworded the string, comma happened.
				let suffix = ''
				if (word.endsWith('.') || word.endsWith(',')) {
					suffix = word[word.length - 1]!
					word = word.substring(0, word.length - 1)
				}
				try {
					return Temporal.Instant.from(word).toZonedDateTimeISO(tz).toLocaleString(locale, {dateStyle: "medium"}) + suffix
				} catch (e) {
					return word + suffix
				}
			}).join(' ')}</p> : <></>}
			<StripePaymentElement/>
		</div>
		{confirm?.error ? <div className="row" style={{flex: '0 0'}}><p className="danger">{confirm?.error?.message}</p></div> : <></>}
		<div className="row">
			<SubmitButton mutation={confirm} vars={() => {}} onSuccess={cancelDialog}>
				Pay {(amountToPay/100).toLocaleString(locale, {style: 'currency', currency: 'EUR'})}
			</SubmitButton>
			<CancelButton>Cancel</CancelButton>
		</div>
	</form>
}

export function useBuyProduct({productID, consent}: {productID: ProductID, consent?: OnboardingConsentAcceptance}) {
	const finalization = useStripeFinalizationState()
	const dialog = useDeclareImagoIntent({verb: 'buy', productID, consent})
	return {
		...dialog,
		isPending: finalization.isPending || dialog.isPending,
	}
}

export function usePayInvoice({invoiceID}: {invoiceID: InvoiceID}) {
	const finalization = useStripeFinalizationState()
	const dialog = useDeclareImagoIntent({verb: 'pay', invoiceID})
	return {
		...dialog,
		isPending: finalization.isPending || dialog.isPending,
	}
}
